"use client";

import { Typography } from "@mui/material";
import { ArrowDown, ArrowUp } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState } from "react";
import type { BalanceToken } from "./types";
interface BalanceItemProps {
  token: BalanceToken;
  disableNavigation?: boolean;
  canClaimReward?: boolean;
  isKycApproved?: boolean;
}

export function BalanceItem({
  token,
  disableNavigation = false,
  canClaimReward = false,
  isKycApproved = false,
}: BalanceItemProps) {
  const router = useRouter();
  const [imageError, setImageError] = useState(false);

  const isFakeImx = disableNavigation;

  const handleClick = () => {
    if (disableNavigation) {
      return; // Don't navigate if navigation is disabled
    }

    if (token.symbol.toLowerCase() === "imx") {
      router.push("/tokens/imx");
    } else if (token.contractAddress) {
      router.push(`/tokens/${token.contractAddress}`);
    } else {
      router.push("/balance");
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const formatPercentageChange = () => {
    if (typeof token.dailyChange !== "number") {
      return "N/A";
    }
    return `${Math.abs(token.dailyChange).toFixed(1)}%`;
  };

  const getChangeColor = () => {
    if (typeof token.dailyChange !== "number") {
      return "text-blue-400"; // Neutral blue for N/A
    }
    return token.dailyChange >= 0 ? "text-green-400" : "text-red-400";
  };

  return (
    <button
      type="button"
      className={`group relative overflow-hidden py-2 px-4 rounded-lg cursor-pointer transition-all w-full text-left bg-transparent ${
        isFakeImx
          ? "border border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20 hover:border-blue-500/70"
          : "border-none hover:bg-white/5"
      }`}
      onClick={handleClick}
    >
      {isFakeImx && (
        <div className="absolute -left-16 top-0 h-full w-12 rotate-[30deg] scale-y-150 bg-white/10 transition-all duration-700 group-hover:left-[calc(100%+1rem)]" />
      )}
      <div className="flex gap-3">
        {/* Token Icon - spans both rows */}
        <div className="w-8 h-8 rounded-lg bg-center bg-cover bg-no-repeat flex items-center justify-center overflow-hidden bg-white/5 flex-shrink-0 self-start">
          {token.imageUrl && !imageError ? (
            <Image
              unoptimized
              src={token.imageUrl}
              alt={token.symbol}
              width={32}
              height={32}
              className="object-contain rounded-lg"
              onError={handleImageError}
            />
          ) : (
            <div className="w-full h-full bg-white/10 flex items-center justify-center text-white/60 text-xs font-bold rounded-lg">
              {token.symbol.slice(0, 2).toUpperCase()}
            </div>
          )}
        </div>

        {/* Content area with table for alignment */}
        <div className="flex-1 min-w-0">
          <table className="w-full table-fixed">
            <tbody>
              {/* First Row: Name | Balance | USD Value */}
              <tr>
                <td className="w-[35%] align-middle pb-0">
                  <div className="font-medium text-white text-sm">
                    {token.symbol}
                  </div>
                </td>
                <td className="w-[25%] text-right align-middle pb-0 pr-4">
                  <div className="font-medium text-white text-sm">
                    {token.balance}
                  </div>
                </td>
                {isFakeImx && canClaimReward ? (
                  <td className="flex justify-end">
                    <Typography
                      align="right"
                      variant="caption"
                      color="text.secondary"
                      className="transition-colors group-hover:text-blue-400"
                    >
                      <span
                        className={
                          isKycApproved
                            ? "bg-gradient-text bg-clip-text text-transparent font-bold"
                            : "font-bold"
                        }
                      >
                        Claim
                      </span>
                    </Typography>
                  </td>
                ) : (
                  <td className="w-[40%] text-right align-middle pb-0">
                    <div className="font-medium text-white text-sm">
                      {token.fiatEquivalentPrice
                        ? `${token.fiatEquivalentPrice.prefixSymbol}${token.fiatEquivalentPrice.amount}`
                        : token.currentPrice &&
                            Number(token.currentPrice) >= 0.01
                          ? `${Number(token.currentPrice).toFixed(2)}`
                          : ""}
                    </div>
                  </td>
                )}
              </tr>

              {/* Second Row: Exchange Rate | Empty | Price Change */}
              <tr>
                <td className="w-[35%] align-middle pt-0">
                  {token.currentPrice && Number(token.currentPrice) >= 0.01 ? (
                    <div className="font-normal text-white/60 text-xs whitespace-nowrap">
                      {`${Number(token.currentPrice).toFixed(2)} USD`}
                    </div>
                  ) : null}
                </td>
                <td className="w-[25%] text-right align-middle pt-0 pr-4">
                  {/* Empty */}
                </td>
                {isFakeImx && canClaimReward ? (
                  <td className="flex justify-end">
                    <Typography
                      align="right"
                      variant="caption"
                      color="text.secondary"
                      className="transition-colors group-hover:text-blue-400"
                    >
                      <span
                        className={
                          isKycApproved
                            ? "bg-gradient-text bg-clip-text text-transparent font-bold"
                            : "font-bold"
                        }
                      >
                        NOW
                      </span>
                    </Typography>
                  </td>
                ) : (
                  <td className="w-[40%] text-right align-middle pt-0">
                    <div className="flex gap-1 items-center justify-end">
                      {typeof token.dailyChange === "number" &&
                        Math.abs(token.dailyChange) > 0 && (
                          <>
                            <div className="w-3 h-3 flex items-center justify-center">
                              {token.dailyChange > 0 ? (
                                <ArrowUp className="w-3 h-3 text-green-400" />
                              ) : (
                                <ArrowDown className="w-3 h-3 text-red-400" />
                              )}
                            </div>
                            <div
                              className={`font-normal text-xs text-right ${getChangeColor()}`}
                            >
                              {formatPercentageChange()}
                            </div>
                          </>
                        )}
                    </div>
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </button>
  );
}
